import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { opsApi, money } from "../../lib/api";

/** The bill of material behind one budget row — the estimate lines and the FRS items linked to
 *  them. Read-only by design: money moves on the estimate or on a change order, never here. */
export default function BudgetRowDetail({ project, row }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    let live = true;
    opsApi.budgetRowLines(project.id, row.id)
      .then((d) => { if (live) setData(d); })
      .catch(() => toast.error("Could not load the lines behind that row"));
    return () => { live = false; };
  }, [project.id, row.id]);

  if (!data) {
    return <div className="flex items-center gap-2 py-3 text-xs text-slate-500">
      <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading the lines behind this row…
    </div>;
  }

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        {data.lines.length} estimate line{data.lines.length === 1 ? "" : "s"} ·
        cost {money(data.totals.cost)} · sell {money(data.totals.sell)}
        {data.totals.hours ? ` · ${data.totals.hours} hrs` : ""}
        {data.frs_items.length ? ` · ${data.frs_items.length} FRS rows (${data.totals.frs_units} units)` : ""}
      </p>
      <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
            <tr>
              <th className="px-3 py-1.5 text-left">Item</th>
              <th className="px-3 py-1.5 text-left">Manufacturer</th>
              <th className="px-3 py-1.5 text-left">Item #</th>
              <th className="px-3 py-1.5 text-right">Qty</th>
              <th className="px-3 py-1.5 text-right">Unit cost</th>
              <th className="px-3 py-1.5 text-right">Cost</th>
              <th className="px-3 py-1.5 text-right">Sell</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {data.lines.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-4 text-center text-slate-500">
                No estimate lines behind this row.
              </td></tr>
            )}
            {data.lines.map((l) => (
              <tr key={l.id} data-testid={`ops-budget-bom-line-${l.id}`}>
                <td className="px-3 py-1.5">{l.description || "—"}</td>
                <td className="px-3 py-1.5">{l.manufacturer || "—"}</td>
                <td className="px-3 py-1.5 font-mono">{l.item_number || "—"}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{l.qty} {l.uom}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{money(l.unit_cost)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{money(l.cost)}</td>
                <td className="px-3 py-1.5 text-right tabular-nums">{money(l.sell)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {data.frs_items.length > 0 && (
        <div className="rounded-lg border border-slate-200 bg-white p-3" data-testid="ops-budget-bom-frs">
          <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            FRS rooms on these lines
          </p>
          <div className="flex flex-wrap gap-1.5">
            {data.frs_items.slice(0, 60).map((f) => (
              <span key={f.id} className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-600">
                {[f.building, f.floor, f.room_number].filter(Boolean).join(" · ")} — {f.product_code || f.product_description} × {f.quantity}
              </span>
            ))}
            {data.frs_items.length > 60 && (
              <span className="text-[11px] text-slate-400">+{data.frs_items.length - 60} more</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
